import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useCITranslate } from '../../i18n/customerIdentity';
import {
  useCiAdminBasePath,
} from '../../contexts/CiAdminAuthContext';
import { useCiSiteCatalog } from '../../contexts/CiSiteFilterContext';
import CiPageChrome from '../../components/CustomerIdentity/CiPageChrome';
import CiPaginationBar from '../../components/CustomerIdentity/CiPaginationBar';
import {
  CIConfirmPopup,
  CIDrawer,
  CIInfoBar,
} from '../../components/CustomerIdentity/CustomerIdentityShared';
import CustomerForm from '../../components/CustomerIdentity/CustomerForm';
import EntitySettingsPanel from '../../components/CustomerIdentity/EntitySettingsPanel';
import {
  createCustomer,
  deleteCustomer,
  listCustomers,
} from '../../services/customerIdentityApi';
import { reindexCustomerDirectoryRows } from '../../services/customerImportApi';

const errorMessage = (err) => ({ kind: 'danger', error: err });
const successMessage = (i18nKey) => ({ kind: 'success', i18nKey });

const PAGE_SIZE = 25;

function formatBillToAddress(c) {
  const line1 = [c.billto_address_line1, c.billto_address_line2].filter(Boolean).join(', ');
  const cityState = [c.billto_city, c.billto_state_province].filter(Boolean).join(', ');
  const tail = [cityState, c.billto_postal_code].filter(Boolean).join(' ');
  return [line1, tail].filter(Boolean).join(' — ') || null;
}

export default function CustomersAdmin() {
  const t = useCITranslate();
  const navigate = useNavigate();
  const basePath = useCiAdminBasePath();
  const [searchParams] = useSearchParams();
  const { sites } = useCiSiteCatalog();
  const siteFilter = searchParams.get('site_id') || '';
  const querySuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : '';

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pageMessage, setPageMessage] = useState(null);
  const [createDrawerMessage, setCreateDrawerMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const settingsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        paginated: true,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (siteFilter) params.site_id = siteFilter;
      if (search) params.search = search;
      const data = await listCustomers(params);
      setCustomers(data?.items || []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      setPageMessage({
        kind: 'danger',
        i18nKey: 'customers.couldNotLoad',
        error: err,
      });
      setCustomers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [siteFilter, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [siteFilter, search]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const onPrevPage = () => setPage((p) => Math.max(0, p - 1));
  const onNextPage = () => setPage((p) => p + 1);

  useEffect(() => {
    if (!creating) setCreateDrawerMessage(null);
  }, [creating]);

  const handleCreate = async (values) => {
    setBusy(true);
    setCreateDrawerMessage(null);
    try {
      const saved = await createCustomer(values);
      try {
        await settingsRef.current?.flush(saved?.id);
      } catch (settingsErr) {
        // Customer is already created. Surface the settings error inline so
        // the admin can retry from the panel without losing the new customer.
        setCreateDrawerMessage(errorMessage(settingsErr));
        return;
      }
      // Real-time reindex so this customer is searchable in OrderX's hybrid
      // typeahead immediately, not just after the next delta/full reindex.
      // Fire-and-forget: reindexCustomerDirectoryRows never throws, and a
      // failed background reindex must never roll back a successful save.
      reindexCustomerDirectoryRows('customer', [saved?.id]);
      setCreating(false);
      await load();
      setPageMessage(successMessage('customers.created'));
    } catch (err) {
      setCreateDrawerMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteCustomer(pendingDelete.id);
      setPendingDelete(null);
      await load();
      setPageMessage(successMessage('customers.deleted'));
    } catch (err) {
      setPageMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (searchParams.get('tab') === 'user-groups') {
    const q = new URLSearchParams();
    const sid = searchParams.get('site_id');
    if (sid) q.set('site_id', sid);
    return (
      <Navigate
        to={`${basePath}/user-groups${q.toString() ? `?${q}` : ''}`}
        replace
      />
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      <CIInfoBar message={pageMessage} onDismiss={() => setPageMessage(null)} />
      <CiPageChrome
        title={t('customers.pageTitle')}
        subtitle={t('customers.chromeSubtitle')}
        actions={
          <button
            type="button"
            className="btn ci-btn-primary text-white"
            onClick={() => setCreating(true)}
          >
            <i className="bi bi-plus-lg me-1" />
            {t('customers.newCustomer')}
          </button>
        }
      >
        <div className="container-fluid px-0 px-md-1">
              <div className="ci-card mb-3">
                <div className="p-3">
                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-8">
                      <label className="form-label">{t('customers.filters.search')}</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t('customers.filters.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-4 d-flex justify-content-md-end">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={load}
                        disabled={loading}
                      >
                        {loading
                          ? t('customers.filters.searching')
                          : t('customers.filters.searchAction')}
                      </button>
                    </div>
                  </div>
                  <p className="text-muted small mb-0 mt-2">
                    {total} customer{total === 1 ? '' : 's'} total
                  </p>
                </div>
              </div>

              <div className="ci-card ci-card-flush">
                <CiPaginationBar
                  position="top"
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  loading={loading}
                  onPrevPage={onPrevPage}
                  onNextPage={onNextPage}
                />
                <div className="table-responsive">
                  <table className="table m-0">
                    <thead>
                      <tr>
                        <th>{t('customers.columns.name')}</th>
                        <th>{t('customers.columns.type')}</th>
                        <th>{t('customers.columns.email')}</th>
                        <th>Bill-To Address</th>
                        <th>{t('customers.columns.site')}</th>
                        <th>{t('common.status')}</th>
                        <th className="text-end">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.length === 0 && !loading ? (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">
                            {t('customers.empty')}
                          </td>
                        </tr>
                      ) : null}
                      {customers.map((c) => {
                        const site = sites.find((s) => String(s.id) === String(c.site_id));
                        return (
                          <tr key={c.id}>
                            <td>
                              <div className="fw-semibold">{c.name}</div>
                              <div className="text-muted small">id: {c.id}</div>
                            </td>
                            <td>
                              {t(`customer.accountTypes.${c.account_type}`) || c.account_type}
                              {c.is_2fa_required ? (
                                <span className="badge bg-warning ms-1">
                                  {t('customer.requires2fa')}
                                </span>
                              ) : null}
                            </td>
                            <td>{c.email || '—'}</td>
                            <td className="small">
                              {formatBillToAddress(c) || <span className="text-muted">—</span>}
                              {c.billto_country_code && c.billto_country_code !== 'US' ? (
                                <span className="text-muted"> ({c.billto_country_code})</span>
                              ) : null}
                            </td>
                            <td>{site ? site.name : c.site_id}</td>
                            <td>
                              <span
                                className={`badge ${
                                  c.status === 'active'
                                    ? 'bg-success'
                                    : c.status === 'suspended'
                                    ? 'bg-warning'
                                    : 'bg-secondary'
                                }`}
                              >
                                {t(`customer.statuses.${c.status}`) || c.status}
                              </span>
                            </td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() =>
                                    navigate(
                                      `${basePath}/customers/${c.id}${querySuffix}`,
                                    )
                                  }
                                >
                                  {t('customers.open')}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => setPendingDelete(c)}
                                >
                                  {t('common.delete')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <CiPaginationBar
                  position="bottom"
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  loading={loading}
                  onPrevPage={onPrevPage}
                  onNextPage={onNextPage}
                />
              </div>
        </div>
      </CiPageChrome>

      <CIDrawer
        open={creating}
        title={t('customers.newCustomer')}
        onClose={() => setCreating(false)}
      >
        <CustomerForm
          sites={sites}
          defaultSiteId={siteFilter || undefined}
          onSubmit={handleCreate}
          onCancel={() => setCreating(false)}
          busy={busy}
          formMessage={createDrawerMessage}
          onDismissMessage={() => setCreateDrawerMessage(null)}
        >
          <EntitySettingsPanel
            ref={settingsRef}
            scope="customer"
            scopeId={null}
            contextSiteId={siteFilter || null}
            onError={(err) => setCreateDrawerMessage(errorMessage(err))}
          />
        </CustomerForm>
      </CIDrawer>

      <CIConfirmPopup
        open={Boolean(pendingDelete)}
        title={t('customer.deleteConfirmTitle')}
        message={t('customer.deleteConfirm', { name: pendingDelete?.name || '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
